import type { ReactNode } from 'react';
import { Botao } from './primitivos.js';
import { Icone } from './Icone.js';

/**
 * Carregando, vazio e erro — definidos UMA vez.
 *
 * O documento 05 é explícito: estes três estados fazem parte do
 * sistema, não de cada tela. A razão prática é que eles são os
 * primeiros a serem improvisados sob pressão, e o resultado é uma tela
 * com spinner, outra com "Carregando…", uma terceira sem nada, e uma
 * quarta que mostra a mensagem crua do servidor para o usuário final.
 *
 * Ver: Arena_backend/Docs/plano/05-front-fundacao.md
 */

// ---------------------------------------------------------------------
// Carregando
// ---------------------------------------------------------------------

/**
 * Esqueleto no lugar de spinner.
 *
 * O esqueleto ocupa o espaço que o conteúdo vai ocupar, então a tela
 * não pula quando o dado chega. Spinner centralizado não reserva
 * espaço e produz exatamente esse salto.
 */
export function Esqueleto({
  altura = 14,
  largura = '100%',
  className,
}: {
  altura?: number | string;
  largura?: number | string;
  className?: string;
}) {
  return (
    <span
      className={['esqueleto', className].filter(Boolean).join(' ')}
      style={{ height: altura, width: largura }}
      aria-hidden="true"
    />
  );
}

export function EsqueletoDeTabela({
  linhas = 6,
  colunas = 5,
}: {
  linhas?: number;
  colunas?: number;
}) {
  return (
    <div className="tbl-scroll" aria-busy="true" aria-live="polite">
      <span className="sr-only">Carregando…</span>
      <table className="tbl">
        <tbody>
          {Array.from({ length: linhas }, (_, l) => (
            <tr key={l}>
              {Array.from({ length: colunas }, (_, c) => (
                <td key={c}>
                  <Esqueleto largura={c === 0 ? '70%' : '45%'} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EsqueletoDeCard({ linhas = 3 }: { linhas?: number }) {
  return (
    <div className="card" aria-busy="true">
      <Esqueleto altura={18} largura="40%" />
      <div style={{ display: 'grid', gap: 10, marginTop: 16 }}>
        {Array.from({ length: linhas }, (_, i) => (
          <Esqueleto key={i} largura={i === linhas - 1 ? '60%' : '100%'} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Erro
// ---------------------------------------------------------------------

/**
 * Erro que diz O QUE FAZER.
 *
 * "Algo deu errado" não é mensagem de erro, é desculpa. As três
 * variantes cobrem os casos que o usuário pode resolver — e a de
 * permissão existe porque 403 não é falha, é resposta: a tela precisa
 * dizer que a pessoa não tem acesso, não que o sistema quebrou.
 */
export function Erro({
  tipo = 'falha',
  detalhe,
  codigo,
  onTentarDeNovo,
}: {
  tipo?: 'falha' | 'permissao' | 'rede';
  detalhe?: ReactNode;
  /** O `requestId` que o backend devolve. É o que o suporte procura. */
  codigo?: string;
  onTentarDeNovo?: () => void;
}) {
  const textos = {
    falha: {
      titulo: 'Não consegui carregar isto',
      corpo: 'A tentativa falhou no servidor. Tentar de novo costuma resolver.',
    },
    permissao: {
      titulo: 'Você não tem acesso a isto',
      corpo: 'Seu perfil não permite esta ação. Peça ao Dono da arena para liberar.',
    },
    rede: {
      titulo: 'Sem conexão com o servidor',
      corpo: 'Verifique a internet da recepção e tente de novo.',
    },
  }[tipo];

  return (
    <div className="empty" role="alert" style={{ borderColor: 'var(--line)' }}>
      <Icone nome={tipo === 'permissao' ? 'lock' : 'info'} tamanho={22} />
      <div style={{ marginTop: 10, color: 'var(--txt)', fontWeight: 600 }}>{textos.titulo}</div>
      <div style={{ marginTop: 4 }}>{detalhe ?? textos.corpo}</div>
      {codigo && (
        <div style={{ marginTop: 8, fontSize: 11.5 }}>
          Código para o suporte: <span className="num">{codigo}</span>
        </div>
      )}
      {onTentarDeNovo && tipo !== 'permissao' && (
        <div style={{ marginTop: 14 }}>
          <Botao variante="contorno" pequeno onClick={onTentarDeNovo}>
            Tentar de novo
          </Botao>
        </div>
      )}
    </div>
  );
}
